import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, IconButton, InputLabel, Menu, MenuItem,
  Paper, Select, Table, TableBody, TableCell, TableHead, TableRow, TextField,
  ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material'
import PersonAddIcon       from '@mui/icons-material/PersonAdd'
import MoreVertIcon        from '@mui/icons-material/MoreVert'
import EditIcon            from '@mui/icons-material/EditOutlined'
import KeyIcon             from '@mui/icons-material/VpnKey'
import SendIcon            from '@mui/icons-material/Send'
import BlockIcon           from '@mui/icons-material/Block'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined'
import DeleteIcon          from '@mui/icons-material/DeleteOutlined'
import { supabase } from '../../supabaseClient'
import { useAuth } from '../../auth/AuthContext'
import { readFunctionError } from '../../lib/functionError'
import { User, UserRole, UserStatus } from '../../types'
import EditStaffDialog        from '../../components/staff/EditStaffDialog'
import SetStaffPasswordDialog from '../../components/staff/SetStaffPasswordDialog'

const STAFF_ROLES: UserRole[] = ['admin', 'office_manager', 'instructor', 'mentor']

const ROLE_LABELS: Record<string, string> = {
  admin:          'Admin',
  office_manager: 'Manager',
  instructor:     'Instructor',
  mentor:         'Mentor',
  all:            'All',
}

const ROLE_COLOR: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info'> = {
  admin:          'error',
  office_manager: 'info',
  instructor:     'primary',
  mentor:         'secondary',
}

const STATUS_COLOR: Record<UserStatus, 'default' | 'success' | 'warning' | 'error'> = {
  active:             'success',
  inactive:           'default',
  suspended:          'error',
  pending_activation: 'warning',
}

const STATUS_LABELS: Record<UserStatus, string> = {
  active:             'Active',
  inactive:           'Inactive',
  suspended:          'Suspended',
  pending_activation: 'Pending',
}

type Filter = 'all' | UserRole

interface AddForm {
  email:      string
  first_name: string
  last_name:  string
  role:       UserRole
}

function fmtDate(s: string | null) {
  if (!s) return '—'
  return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function StaffPage() {
  const { session, role: callerRole } = useAuth()
  const [users,     setUsers]     = useState<User[]>([])
  const [loading,   setLoading]   = useState(true)
  const [filter,    setFilter]    = useState<Filter>('all')
  const [search,    setSearch]    = useState('')
  const [error,     setError]     = useState<string | null>(null)
  const [success,   setSuccess]   = useState<string | null>(null)

  const [open,    setOpen]    = useState(false)
  const [form,    setForm]    = useState<AddForm>({ email: '', first_name: '', last_name: '', role: 'mentor' })
  const [saving,  setSaving]  = useState(false)
  const [saveErr, setSaveErr] = useState<string | null>(null)

  // Action menu / dialogs
  const [menuAnchor,    setMenuAnchor]    = useState<null | HTMLElement>(null)
  const [menuRow,       setMenuRow]       = useState<User | null>(null)
  const [editTarget,    setEditTarget]    = useState<User | null>(null)
  const [pwdTarget,     setPwdTarget]     = useState<User | null>(null)
  const [confirmSuspend, setConfirmSuspend] = useState<User | null>(null)
  const [confirmDelete,  setConfirmDelete]  = useState<User | null>(null)
  const [actioning,      setActioning]      = useState<string | null>(null)

  async function load() {
    setLoading(true)
    const { data } = await supabase
      .from('users')
      .select('*')
      .in('role', STAFF_ROLES)
      .order('created_at', { ascending: false })
    setUsers(data ?? [])
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  const visible = useMemo(() => {
    let list = users
    if (filter !== 'all') list = list.filter(u => u.role === filter)
    if (search) {
      const q = search.toLowerCase()
      list = list.filter(u =>
        u.email.toLowerCase().includes(q) ||
        u.first_name.toLowerCase().includes(q) ||
        u.last_name.toLowerCase().includes(q)
      )
    }
    return list
  }, [users, filter, search])

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: users.length }
    for (const r of STAFF_ROLES) c[r] = users.filter(u => u.role === r).length
    return c
  }, [users])

  /** Permission helpers — admin can do anything; office_manager can do
   *  anything except touching admins. Either role cannot self-delete /
   *  self-suspend. */
  const isAdmin = callerRole === 'admin'
  function canEdit(target: User)        { return isAdmin || target.role !== 'admin' }
  function canSetPassword(target: User) { return canEdit(target) && target.user_id !== session?.user.id }
  function canSuspend(target: User)     { return canEdit(target) && target.user_id !== session?.user.id }
  function canDelete(target: User)      { return canEdit(target) && target.user_id !== session?.user.id }

  function set<K extends keyof AddForm>(field: K) {
    return (val: AddForm[K]) => setForm(f => ({ ...f, [field]: val }))
  }

  async function handleAdd() {
    if (!session) return
    if (!form.email.trim() || !form.first_name.trim() || !form.last_name.trim()) {
      setSaveErr('Name and email are required.'); return
    }
    setSaving(true)
    setSaveErr(null)
    const { data: json, error: invokeErr } = await supabase.functions.invoke('invite-user', {
      body: {
        email:      form.email.trim().toLowerCase(),
        first_name: form.first_name.trim(),
        last_name:  form.last_name.trim(),
        role:       form.role,
        invited_by: session.user.id,
      },
    })
    if (invokeErr) { setSaveErr(await readFunctionError(invokeErr)); setSaving(false); return }
    if (json?.error) { setSaveErr(json.error); setSaving(false); return }
    setSaving(false)
    setSuccess(json?.already_existed
      ? `${form.first_name} already had an account — invitation re-sent.`
      : `Invitation sent to ${form.email.trim().toLowerCase()}.`)
    setOpen(false)
    setForm({ email: '', first_name: '', last_name: '', role: 'mentor' })
    load()
  }

  async function resendInvite(user: User) {
    setActioning(user.user_id)
    await supabase.functions.invoke('resend-invite', { body: { email: user.email } })
    setActioning(null)
    setSuccess(`Invitation re-sent to ${user.email}.`)
  }

  async function toggleSuspend(user: User) {
    setConfirmSuspend(null)
    const next: UserStatus = user.status === 'suspended' ? 'active' : 'suspended'
    setActioning(user.user_id)
    const { error: dbErr } = await supabase.from('users').update({ status: next }).eq('user_id', user.user_id)
    setActioning(null)
    if (dbErr) setError(dbErr.message)
    else setUsers(prev => prev.map(u => u.user_id === user.user_id ? { ...u, status: next } : u))
  }

  async function doDelete(user: User) {
    setConfirmDelete(null)
    setActioning(user.user_id)
    const { data, error: invokeErr } = await supabase.functions.invoke('delete-user', {
      body: { user_id: user.user_id },
    })
    setActioning(null)
    if (invokeErr) setError(await readFunctionError(invokeErr))
    else if (data?.error) setError(data.error)
    else setUsers(prev => prev.filter(u => u.user_id !== user.user_id))
  }

  function openMenu(e: React.MouseEvent<HTMLElement>, row: User) {
    setMenuAnchor(e.currentTarget); setMenuRow(row)
  }
  function closeMenu() {
    setMenuAnchor(null); setMenuRow(null)
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 1200 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.25 }}>Staff</Typography>
          <Typography variant="body2" color="text.secondary">
            Admins, office managers, instructors, and mentors who run the program.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => { setOpen(true); setSaveErr(null); setForm({ email: '', first_name: '', last_name: '', role: 'mentor' }) }}
        >
          Invite staff
        </Button>
      </Box>

      {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <ToggleButtonGroup exclusive size="small" value={filter} onChange={(_, v) => v && setFilter(v)}>
          <ToggleButton value="all" sx={{ textTransform: 'none' }}>All ({counts.all ?? 0})</ToggleButton>
          {STAFF_ROLES.map(r => (
            <ToggleButton key={r} value={r} sx={{ textTransform: 'none' }}>
              {ROLE_LABELS[r]} ({counts[r] ?? 0})
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <TextField
          size="small" placeholder="Search by name or email…"
          value={search} onChange={e => setSearch(e.target.value)}
          sx={{ minWidth: 260, ml: 'auto' }}
        />
      </Box>

      <Paper>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
        ) : visible.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">{search ? 'No matches.' : 'No staff in this filter.'}</Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Joined</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Last Login</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 50 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map(u => {
                const isSelf = u.user_id === session?.user.id
                return (
                  <TableRow key={u.user_id} hover>
                    <TableCell sx={{ fontWeight: 500 }}>
                      {u.first_name} {u.last_name}
                      {isSelf && <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>(you)</Typography>}
                    </TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      <Chip label={ROLE_LABELS[u.role] ?? u.role} size="small" color={ROLE_COLOR[u.role] ?? 'default'} />
                    </TableCell>
                    <TableCell>
                      <Chip label={STATUS_LABELS[u.status]} size="small" color={STATUS_COLOR[u.status]} />
                    </TableCell>
                    <TableCell><Typography variant="caption">{fmtDate(u.created_at)}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{fmtDate(u.last_login)}</Typography></TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={e => openMenu(e, u)} disabled={actioning === u.user_id}>
                        {actioning === u.user_id ? <CircularProgress size={16} /> : <MoreVertIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        {menuRow && canEdit(menuRow) && (
          <MenuItem onClick={() => { setEditTarget(menuRow); closeMenu() }}>
            <EditIcon fontSize="small" sx={{ mr: 1.5 }} /> Edit details
          </MenuItem>
        )}
        {menuRow && canSetPassword(menuRow) && (
          <MenuItem onClick={() => { setPwdTarget(menuRow); closeMenu() }}>
            <KeyIcon fontSize="small" sx={{ mr: 1.5 }} /> Set password
          </MenuItem>
        )}
        {menuRow?.status === 'pending_activation' && canEdit(menuRow) && (
          <MenuItem onClick={() => { menuRow && resendInvite(menuRow); closeMenu() }}>
            <SendIcon fontSize="small" sx={{ mr: 1.5 }} /> Resend invite
          </MenuItem>
        )}
        {menuRow && canSuspend(menuRow) && (
          <MenuItem onClick={() => {
            if (!menuRow) return
            if (menuRow.status === 'suspended') toggleSuspend(menuRow)
            else setConfirmSuspend(menuRow)
            closeMenu()
          }}>
            {menuRow.status === 'suspended'
              ? <><CheckCircleOutlineIcon fontSize="small" sx={{ mr: 1.5 }} /> Reactivate</>
              : <><BlockIcon fontSize="small" sx={{ mr: 1.5 }} /> Suspend</>}
          </MenuItem>
        )}
        {menuRow && canDelete(menuRow) && (
          <MenuItem onClick={() => { setConfirmDelete(menuRow); closeMenu() }} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1.5 }} /> Delete account
          </MenuItem>
        )}
        {menuRow && !canEdit(menuRow) && !canDelete(menuRow) && (
          <MenuItem disabled>
            <Typography variant="caption">No actions available for this role.</Typography>
          </MenuItem>
        )}
      </Menu>

      <EditStaffDialog
        open={!!editTarget}
        staff={editTarget}
        callerRole={callerRole}
        onClose={() => setEditTarget(null)}
        onSaved={load}
      />

      <SetStaffPasswordDialog
        open={!!pwdTarget}
        staff={pwdTarget}
        onClose={() => setPwdTarget(null)}
        onSaved={load}
      />

      {/* Invite dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Invite Staff</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            They'll get an email with a link to set their password.
          </Typography>
          {saveErr && <Alert severity="error" sx={{ mb: 2 }}>{saveErr}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="First name" size="small" fullWidth required value={form.first_name} onChange={e => set('first_name')(e.target.value)} />
              <TextField label="Last name"  size="small" fullWidth required value={form.last_name}  onChange={e => set('last_name')(e.target.value)} />
            </Box>
            <TextField label="Email" size="small" type="email" fullWidth required value={form.email} onChange={e => set('email')(e.target.value)} />
            <FormControl size="small" fullWidth required>
              <InputLabel>Role</InputLabel>
              <Select label="Role" value={form.role} onChange={e => set('role')(e.target.value as UserRole)}>
                {STAFF_ROLES.filter(r => isAdmin || r !== 'admin').map(r => (
                  <MenuItem key={r} value={r}>{ROLE_LABELS[r]}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Send Invite'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Suspend confirm */}
      <Dialog open={!!confirmSuspend} onClose={() => setConfirmSuspend(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Suspend account?</DialogTitle>
        <DialogContent>
          <Typography>
            <strong>{confirmSuspend?.first_name} {confirmSuspend?.last_name}</strong> will be signed
            out immediately and unable to log in until reactivated.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmSuspend(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => confirmSuspend && toggleSuspend(confirmSuspend)}>
            Suspend
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete account?</DialogTitle>
        <DialogContent>
          <Typography>
            This will permanently delete <strong>{confirmDelete?.first_name} {confirmDelete?.last_name}</strong>.
            Any class or student assignments referencing them will be affected. Cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => confirmDelete && doDelete(confirmDelete)}>
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
